import React, { useEffect, useState, useRef } from "react";
import { Box, Card, Typography, Skeleton, Button, Avatar, IconButton } from "@mui/material";
import { UserCheck, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PersonHoverCard from "./PersonHoverCard";

const COMMUNITY_API = "https://apiweb.hitpoly.com/ajax/communityWidgetsController.php";
const RELATIONS_API = "https://apiweb.hitpoly.com/ajax/RelacionesControlador.php";

const profileInfoScore = (p) => {
  const fields = [
    p.cover_photo,
    p.avatar,
    p.role || p.cargo_nombre,
    p.bio,
    p.city,
    p.country,
    p.headline,
    p.phone,
    p.website,
  ];
  const arrayFields = [p.experience, p.education, p.skills, p.languages, p.interests];
  let score = fields.filter(Boolean).length * 2;
  arrayFields.forEach(arr => {
    if (Array.isArray(arr)) score += arr.length;
    else if (arr && typeof arr === 'string' && arr.trim().length > 0) score += 1;
  });
  return score;
};

const profilePriorityScore = (p) => {
  const hasBanner = p.cover_photo ? 1 : 0;
  return hasBanner * 10000 + profileInfoScore(p);
};

const ProfessionalProfilesCard = ({ currentUserId }) => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [hoverProf, setHoverProf] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [relationStatuses, setRelationStatuses] = useState({});
  const hoverTimeoutRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };
  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    axios.post(COMMUNITY_API, { action: "getProfessionalProfiles", user_id: currentUserId }, { signal: controller.signal })
      .then(r => {
        if (cancelled) return;
        if (r.data?.success && r.data.data?.length > 0) {
          const sorted = [...r.data.data].sort((a, b) => profilePriorityScore(b) - profilePriorityScore(a));
          setProfessionals(sorted);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [currentUserId]);

  const handleNavigateProfile = (prof) => {
    const profId = prof.id || prof.user_id;
    setHoverProf(null);
    setHoverAnchor(null);
    navigate(`/systems/bolsa/perfil-profesional/${profId}?userId=${currentUserId}`);
  };

  const checkRelationStatus = async (targetId) => {
    if (!currentUserId || !targetId) return { status: "none", isSender: false };
    try {
      const res = await axios.get(RELATIONS_API, {
        params: { action: "status", user_id: currentUserId, target_id: targetId }
      });
      if (res.data?.success) {
        return {
          status: res.data.status || "none",
          isSender: Number(res.data.remitente_original) === Number(currentUserId)
        };
      }
    } catch {}
    return { status: "none", isSender: false };
  };

  useEffect(() => {
    if (!professionals.length) return;
    let mounted = true;
    (async () => {
      const statuses = {};
      await Promise.all(professionals.map(async (p) => {
        if (Number(p.id) !== Number(currentUserId)) {
          statuses[p.id] = await checkRelationStatus(p.id);
        }
      }));
      if (mounted) setRelationStatuses(statuses);
    })();
    return () => { mounted = false; };
  }, [professionals, currentUserId]);

  const handleFriendAction = async (prof, action) => {
    if (!currentUserId || !prof.id) return;
    try {
      const res = await axios.post(RELATIONS_API, {
        user_id: currentUserId,
        target_id: prof.id,
        action: action
      });
      if (res.data?.success) {
        setRelationStatuses(prev => ({
          ...prev,
          [prof.id]: { status: res.data.newStatus, isSender: action === "enviar" }
        }));
        if (action === "enviar") {
          axios.post("https://apiweb.hitpoly.com/ajax/NotificacionesDeSolicitudDeAmistadController.php", {
            user_id: currentUserId,
            target_id: prof.id,
            tipo: "enviar"
          }).catch(() => {});
        }
      }
    } catch {}
  };

  const handleMouseEnter = (e, prof) => {
    clearTimeout(hoverTimeoutRef.current);
    const target = e.currentTarget;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverAnchor(target);
      setHoverProf(prof);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverAnchor(null);
      setHoverProf(null);
    }, 300);
  };

  const getHoverPerson = (prof) => ({
    ...prof,
    nombre: prof.name?.split(' ')[0] || prof.nombre || '',
    apellido: prof.name?.split(' ').slice(1).join(' ') || prof.apellido || '',
    cargo_nombre: prof.role || prof.cargo_nombre || '',
  });

  if (loading) return <Skeleton variant="rectangular" height={220} sx={{ mb: 2, borderRadius: 4 }} />;
  if (professionals.length === 0) return null;

  const getDisplayName = (p) => {
    return p.name || `${p.nombre || ''} ${p.apellido || ''}`.trim() || 'Profesional';
  };

  return (
    <Card sx={{
      bgcolor: "rgba(255,255,255,0.9)",
      backdropFilter: "blur(8px)",
      borderRadius: 4,
      border: '1px solid #f0f0f0',
      boxShadow: 'none',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 300
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
          <UserCheck size={18} color="#3B207F" /> Profesionales
          <Box sx={{ bgcolor: '#f0ecf9', color: '#3B207F', fontSize: '0.75rem', fontWeight: 'bold', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', ml: 0.5 }}>
            {professionals.length}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={scrollLeft} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronLeft size={16} /></IconButton>
          <IconButton size="small" onClick={scrollRight} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronRight size={16} /></IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto', className: 'community-wall-scroll', scrollbarWidth: 'thin', scrollbarColor: '#0B8DB5 transparent',
        overflowY: 'hidden',
        pb: 1.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 4, border: 0 }
      }}>
        {professionals.map(prof => (
          <Card
            key={prof.id}
            sx={{
              minWidth: 160,
              maxWidth: 160,
              p: 2,
              borderRadius: 3,
              border: '1px solid #f0ecf9',
              boxShadow: 'none',
              bgcolor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              '&:hover': {
                borderColor: '#3B207F',
                boxShadow: '0 4px 12px rgba(59, 32, 127, 0.08)'
              }
            }}
            onClick={() => handleNavigateProfile && handleNavigateProfile(prof)}
            onMouseEnter={(e) => handleMouseEnter(e, prof)}
            onMouseLeave={handleMouseLeave}
          >
            <Avatar
              src={prof.avatar || undefined}
              sx={{ width: 56, height: 56, mb: 1.5, border: '2px solid #f0ecf9', bgcolor: '#e8e4f5', color: '#3B207F', fontWeight: 'bold' }}
            >
              {(getDisplayName(prof) || "P").charAt(0)}
            </Avatar>
            <Typography variant="body2" fontWeight="800" color="#1a1a1a" sx={{ mb: 0.5, lineHeight: 1.2 }}> 
              {getDisplayName(prof)}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ lineHeight: 1.3, overflow: 'visible' }}>
              {prof.role || "Profesional en Hitpoly"}
            </Typography>
          </Card>
        ))}
      </Box>

      {hoverProf && hoverAnchor && (
        <PersonHoverCard
          person={getHoverPerson(hoverProf)}
          anchorEl={hoverAnchor}
          currentUserId={currentUserId}
          relationStatus={relationStatuses[hoverProf.id]}
          onClose={() => { setHoverAnchor(null); setHoverProf(null); }}
          onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
          onMouseLeave={handleMouseLeave}
          onFriendAction={(action) => handleFriendAction(hoverProf, action)}
          onNavigateProfile={() => handleNavigateProfile(hoverProf)}
          onNavigateMessage={() => {
            setHoverProf(null);
            setHoverAnchor(null);
            const nombre = getHoverPerson(hoverProf).nombre;
            const apellido = getHoverPerson(hoverProf).apellido;
            window.postMessage({
              type: 'OPEN_FLOATING_CHAT',
              partner: {
                id: hoverProf.id,
                name: `${nombre} ${apellido}`.trim(),
                avatar: hoverProf.avatar || '',
                type: 'professional'
              }
            }, '*');
          }}
        />
      )}

      <Button
        onClick={() => navigate(`/systems/bolsa/profesionales?userId=${currentUserId}`)}
        sx={{
          width: '100%',
          mt: 1,
          textTransform: 'none',
          fontWeight: '700',
          color: '#3B207F',
          '&:hover': { bgcolor: '#f0ecf9' }
        }}
        endIcon={<ArrowRight size={16} />}
      >
        Ver más profesionales
      </Button>
    </Card>
  );
};

export default ProfessionalProfilesCard;

